/**
 * Client-side auth functions for browser use.
 * Ported from dashboard/lib/auth.ts (client-side portions).
 */
import * as Sentry from '@sentry/tanstackstart-react'
import { requestWithCredentials, getApiErrorMessage } from './api'
import { QueryClient } from '@tanstack/react-query'

// Singleton query client reference — set by router.tsx after creation
let _queryClient: QueryClient | null = null
export function setAuthQueryClient(qc: QueryClient) {
  _queryClient = qc
}

export type UserRole = 'ADMIN' | 'USER'
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'none' | 'comped'
export type SubscriptionPlan = 'weekly_trial' | 'monthly' | 'quarterly' | 'yearly'

export interface User {
  id: string
  email: string
  name: string | null
  familyId: string | null
  role: UserRole
  subscriptionStatus?: SubscriptionStatus
  tenantType?: 'family' | 'school'
  tenantId?: string | null
  schoolStaffRole?: 'admin' | 'staff'
  schoolName?: string
}

export interface AuthResponse {
  user: User
  sessionToken: string
  accountType: 'family' | 'school'
  school?: {
    id: string
    name: string
    staffRole: string
  }
}

export interface RegisterResponse {
  message: string
  email?: string
  autoLogin: boolean
  user?: User
}

export interface SubscriptionResponse {
  subscription: {
    status: SubscriptionStatus
    plan: SubscriptionPlan | null
    currentPeriodEnd: string | null
    trialEnd: string | null
    hasActiveSubscription: boolean
    isAdmin: boolean
  }
}

export type ChangeablePlan = Exclude<SubscriptionPlan, 'weekly_trial'>

export interface ChangePlanResponse {
  subscription: {
    id: string
    status: string
    currentPlan: ChangeablePlan
  }
}

export interface PortalSessionResponse {
  url: string
}

export interface CheckoutSessionResponse {
  sessionId: string
  clientSecret?: string
  checkoutUrl?: string
}

export interface CheckoutSessionStatus {
  status: 'pending' | 'complete' | 'expired'
  userCreated: boolean
  isGoogleUser: boolean
  email?: string
  canClaimSession?: boolean
}

export interface StagedUser {
  email: string
  name: string | null
  workosUserId: string
}

export interface SetupUserInfo {
  email: string
  name: string | null
}

export interface CompleteSetupResponse {
  user: User
  sessionToken: string
}

// ---------------------------------------------------------------------------
// Auth functions
// ---------------------------------------------------------------------------

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await requestWithCredentials('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Login failed'))
  }
  const data: AuthResponse = await response.json()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data
}

export async function logout(): Promise<void> {
  try {
    await requestWithCredentials('/api/v1/auth/logout', { method: 'POST' })
  } catch {
    // Ignore errors on logout
  }
  Sentry.setUser(null)
  _queryClient?.clear()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await requestWithCredentials('/api/v1/auth/me')
    if (!response.ok) return null
    const data = await response.json()
    return data.user
  } catch {
    return null
  }
}

export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await requestWithCredentials('/api/v1/billing/subscription')
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to get subscription'))
  }
  return response.json()
}

export async function createPortalSession(returnUrl: string): Promise<PortalSessionResponse> {
  const response = await requestWithCredentials('/api/v1/billing/portal-session', {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to create portal session'))
  }
  return response.json()
}

export async function changePlan(plan: ChangeablePlan): Promise<ChangePlanResponse> {
  const response = await requestWithCredentials('/api/v1/billing/change-plan', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to change plan'))
  }
  return response.json()
}

export async function updateEmail(email: string, currentPassword: string): Promise<User> {
  const response = await requestWithCredentials('/api/v1/auth/email', {
    method: 'PUT',
    body: JSON.stringify({ email, currentPassword }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to update email'))
  }
  const data = await response.json()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data.user
}

export async function updatePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await requestWithCredentials('/api/v1/auth/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to update password'))
  }
}

export async function updateName(name: string): Promise<User> {
  const response = await requestWithCredentials('/api/v1/auth/name', {
    method: 'PUT',
    body: JSON.stringify({ name }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to update name'))
  }
  const data = await response.json()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data.user
}

export async function contactSupport(params: { subject: string; message: string }): Promise<void> {
  const response = await requestWithCredentials('/api/v1/auth/support/contact', {
    method: 'POST',
    body: JSON.stringify(params),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to send support email'))
  }
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await requestWithCredentials('/api/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to send reset email'))
  }
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const response = await requestWithCredentials('/api/v1/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to reset password'))
  }
}

export async function register(
  email: string,
  name: string,
  plan: SubscriptionPlan,
  stripeCustomerId: string,
  paymentMethodId: string,
  workosUserId?: string,
): Promise<RegisterResponse> {
  const response = await requestWithCredentials('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email,
      name,
      plan,
      stripeCustomerId,
      paymentMethodId,
      ...(workosUserId && { workosUserId }),
    }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Registration failed'))
  }
  const data: RegisterResponse = await response.json()
  if (data.autoLogin && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data
}

export async function registerWithPromoCode(
  email: string,
  name: string,
  promoCode: string,
  workosUserId?: string,
): Promise<RegisterResponse> {
  const response = await requestWithCredentials('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email,
      name,
      plan: 'monthly',
      promoCode,
      ...(workosUserId && { workosUserId }),
    }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Registration failed'))
  }
  const data: RegisterResponse = await response.json()
  if (data.autoLogin && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data
}

export async function createCheckoutSession(
  plan: SubscriptionPlan,
  options?: { email?: string; name?: string; workosUserId?: string },
): Promise<CheckoutSessionResponse> {
  const body: Record<string, string> = { plan }
  if (options?.email) body.email = options.email
  if (options?.name) body.name = options.name
  if (options?.workosUserId) body.workosUserId = options.workosUserId
  const response = await requestWithCredentials('/api/v1/billing/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to create checkout session'))
  }
  return response.json()
}

export async function getCheckoutSessionStatus(sessionId: string): Promise<CheckoutSessionStatus> {
  const response = await requestWithCredentials(
    `/api/v1/billing/checkout-session-status?session_id=${encodeURIComponent(sessionId)}`,
  )
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to get checkout status'))
  }
  return response.json()
}

export async function getStagedUser(): Promise<StagedUser | null> {
  try {
    const response = await requestWithCredentials('/api/v1/auth/staged-user')
    if (!response.ok) return null
    return response.json()
  } catch {
    return null
  }
}

export async function clearStagedUser(): Promise<void> {
  try {
    await requestWithCredentials('/api/v1/auth/staged-user', { method: 'DELETE' })
  } catch {
    // Ignore
  }
}

export async function completeAccount(token: string, password: string): Promise<AuthResponse> {
  const response = await requestWithCredentials('/api/v1/auth/complete-account', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to complete account setup'))
  }
  const data: AuthResponse = await response.json()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data
}

export async function validateSetupToken(setupToken: string): Promise<SetupUserInfo | null> {
  try {
    const response = await requestWithCredentials('/api/v1/auth/validate-setup-token', {
      method: 'POST',
      body: JSON.stringify({ setupToken }),
    })
    if (!response.ok) return null
    return response.json()
  } catch {
    return null
  }
}

export async function completeSetupWithPassword(
  setupToken: string,
  password: string,
  name?: string,
): Promise<CompleteSetupResponse> {
  const response = await requestWithCredentials('/api/v1/auth/complete-setup', {
    method: 'POST',
    body: JSON.stringify({ setupToken, password, ...(name ? { name } : {}) }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to complete setup'))
  }
  const data: CompleteSetupResponse = await response.json()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
  return data
}

export async function initiateGoogleSetup(
  setupToken: string,
  name?: string,
  mixpanelDistinctId?: string,
): Promise<{ redirectUrl: string }> {
  const response = await requestWithCredentials('/api/v1/auth/complete-setup-google', {
    method: 'POST',
    body: JSON.stringify({
      setupToken,
      ...(name ? { name } : {}),
      ...(mixpanelDistinctId ? { mixpanelDistinctId } : {}),
    }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to initiate Google sign-in'))
  }
  return response.json()
}

export async function resendSetupEmail(sessionId: string): Promise<{ message: string; email: string; cooldownSeconds: number }> {
  const response = await requestWithCredentials('/api/v1/auth/resend-setup-email', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  })
  if (!response.ok) {
    throw new Error(await getApiErrorMessage(response, 'Failed to resend setup email'))
  }
  return response.json()
}

// ---------------------------------------------------------------------------
// School auth functions
// ---------------------------------------------------------------------------

export async function schoolLogout(): Promise<void> {
  try {
    // Clear both school and family sessions — school-auth/logout only clears
    // wos-school-session, but auth/logout clears wos-session. Both are needed
    // so auth/me returns 401 and the user actually reaches the login page.
    await Promise.all([
      requestWithCredentials('/api/v1/school-auth/logout', { method: 'POST' }),
      requestWithCredentials('/api/v1/auth/logout', { method: 'POST' }),
    ])
  } catch {
    // Ignore errors on logout
  }
  Sentry.setUser(null)
  _queryClient?.clear()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-change'))
  }
}
